// src/pages/ScreenerPage.jsx
// SCREENER-FRAG — shell only. All state/logic moved to hooks; all repeated
// UI blocks moved to src/components/screener/*. This file composes them and
// renders the parts that don't belong anywhere else (header, scan buttons,
// filter bar, results table).
//
// Composition note (the one real design decision made here, not elsewhere):
// useScreenerData and useScreenerSignal do not know about each other or about
// useSavedScans — each hook only knows its own concern and reports events via
// callbacks. This file wires those callbacks together. That's deliberate:
// it means any one of the three can change internally without the other two
// needing to change, which is the entire point of this fragmentation pass.

import React, { useState, useRef, useCallback } from 'react';
import { SCANS, SECTORS, SORT_OPTIONS, GUEST_LIMIT, S } from '../constants/screenerConfig';
import ProvenScreensDropdown from '../components/screener/ProvenScreensDropdown';
import NLSearchBar from '../components/screener/NLSearchBar';
import { signalColor, signalBg, fmtPrice, fmtChange, fmtVolume, fmt52W, fmtVolRatio, volRatioColor } from '../lib/screenerFormatters';
import { useScreenerData } from '../hooks/useScreenerData';
import { useScreenerSignal } from '../hooks/useScreenerSignal';
import ScoreRing from '../components/screener/ScoreRing';
import GuestLockRow from '../components/screener/GuestLockRow';
import CSVGate from '../components/screener/CSVGate';
import ColumnPicker from '../components/screener/ColumnPicker';
import SavedScansPanel from '../components/screener/SavedScansPanel';
import FilterPanel from '../components/screener/FilterPanel';

export default function ScreenerPage({
  isLoggedIn   = false,
  token        = null,
  onRequestInvite,
  onSignIn,
  onNavigate,
}) {
  // Technical columns — stays here: it's rendering state (which extra table
  // columns to show), read by ColumnPicker, the table, and the signal hook.
  const [visibleCols, setVisibleCols] = useState(new Set());
  const [activeProvenScreen, setActiveProvenScreen] = useState(null); // display-only this shell pass

  function handleColToggle(colKey) {
    setVisibleCols(prev => {
      const next = new Set(prev);
      if (next.has(colKey)) next.delete(colKey);
      else next.add(colKey);
      return next;
    });
  }

  // ── Signal logging hook — owns refs, exposes small event-report surface ──
  // Called first: useScreenerData's onFetchComplete callback needs
  // signalHook.scheduleLog to already exist. dataAsOf/marketStatus are NOT
  // passed in here — see useScreenerSignal.js header for why that would be
  // circular. They're synced below via setDataAsOf/setMarketStatus instead.
  const signalHook = useScreenerSignal({ isLoggedIn, visibleCols });

  // ⚠️ BUG FIX (live-testing round, this session): onFetchComplete/onScanUsed
  // were previously inline arrow functions defined directly in this component
  // body — a NEW function identity every render. useScreenerData.js's
  // fetchResults is a useCallback that depends on onFetchComplete, so a new
  // onFetchComplete every render meant a new fetchResults every render, which
  // retriggered its own `useEffect(() => fetchResults(), [fetchResults])` —
  // an infinite loop. Confirmed live: 2,400+ requests against
  // /api/screener/results in the Network tab, climbing continuously.
  // FIX: hold the latest signalHook in a ref (updated every render via direct
  // assignment — safe and standard for refs, no effect needed), and give
  // onFetchComplete/onScanUsed genuinely stable identities (useCallback with
  // an empty dependency array) that read from the ref instead of closing over
  // signalHook directly. This is the actual fix; useScreenerSignal.js's
  // functions are now also individually memoized as defense in depth, but
  // that alone would NOT have stopped this loop — the object returned by the
  // hook is still a new object every render either way, and this component
  // was destructuring fresh inline wrappers around it regardless.
  const signalHookRef = useRef(signalHook);
  signalHookRef.current = signalHook;

  const handleFetchComplete = useCallback((filters, resultCount, tickers) => {
    signalHookRef.current.scheduleLog(filters, resultCount, tickers);
  }, []);

  const handleScanUsedForData = useCallback((scanKey) => {
    signalHookRef.current.recordScanUsed(scanKey);
  }, []);

  // ── Data/filter hook — reports fetch completion + scan usage via callback ──
  const {
    results, loading, error, dataAsOf, marketStatus, isStale,
    activeScan, signalFilter, sectorFilter, sortBy, registryFilters,
    handleScan: dataHandleScan, handleSignalFilter, handleSectorFilter, handleSort,
    handleRegistryFilterChange, applyLoadedScan,
  } = useScreenerData({
    onFetchComplete: handleFetchComplete,
    onScanUsed:      handleScanUsedForData,
  });

  // Push the latest dataAsOf/marketStatus into signalHook's refs whenever
  // they change — this is the one place the two hooks' outputs actually meet.
  React.useEffect(() => { signalHook.setDataAsOf(dataAsOf); },         [dataAsOf]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => { signalHook.setMarketStatus(marketStatus); }, [marketStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleScan(key) {
    dataHandleScan(key);
  }

  function handleFieldFilterChange(key, value) {
    handleRegistryFilterChange(key, value);
    const field = registryFilters[key];
    signalHook.recordFieldFilterChange(key, field?.category || 'unknown', value);
  }

  function handleLoadScan(scan) {
    applyLoadedScan(scan.filters);
    signalHook.setSavedScanUsed(scan._id);
  }

  function handleTickerRowClick(ticker) {
    signalHook.recordTickerClick(ticker);
    onNavigate && onNavigate('stock', { ticker });
  }

  const hasFilters     = activeScan || signalFilter !== 'ALL' || sectorFilter !== 'ALL';
  const visibleResults = isLoggedIn ? results : results.slice(0, GUEST_LIMIT);
  const hiddenCount     = isLoggedIn ? 0 : Math.max(0, results.length - GUEST_LIMIT);
  const displayCount    = isLoggedIn ? results.length : visibleResults.length;

  const baseColCount  = 8;
  const extraColCount = visibleCols.size;
  const totalColCount = baseColCount + extraColCount;

  return (
    <div style={{ padding: '24px 16px', fontFamily: 'system-ui,sans-serif' }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: S.text, margin: '0 0 4px 0' }}>
          Market Screener
        </h1>
        <p style={{ fontSize: 13, color: S.muted, margin: 0 }}>
          Algorithmic signals across 20 tracked tickers · Not investment advice
        </p>
      </div>

      {/* ── Natural Language search shell (SCREENER-NL-SHELL) — full-width, prominent per
           owner-directed placement. Captures real signal, doesn't fake understanding it yet. ── */}
      <NLSearchBar />

      {/* ── Preset scans + Proven Screens dropdown (same conceptual row — both are
           "load a strategy lens" controls, not attribute filters like Signal/Sector below) ── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 16 }}>
        <ProvenScreensDropdown onSelectScreen={setActiveProvenScreen} activeScreenKey={activeProvenScreen} />
        {SCANS.map(s => (
          <button
            key={s.key}
            onClick={() => handleScan(s.key)}
            title={s.desc}
            style={{
              padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
              border: activeScan === s.key ? `1px solid ${S.purple}` : `1px solid ${S.border2}`,
              background: activeScan === s.key ? 'rgba(99,102,241,0.15)' : 'transparent',
              color: activeScan === s.key ? S.purple : S.muted,
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* ── Filter bar ── */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <select
          value={signalFilter}
          onChange={e => handleSignalFilter(e.target.value)}
          style={{
            padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            background: S.surface, color: S.text, border: `1px solid ${S.border2}`,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {['ALL','BULLISH','NEUTRAL','BEARISH'].map(v => (
            <option key={v} value={v}>{v === 'ALL' ? 'All Signals' : v}</option>
          ))}
        </select>

        <select
          value={sectorFilter}
          onChange={e => handleSectorFilter(e.target.value)}
          style={{
            padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            background: S.surface, color: S.text, border: `1px solid ${S.border2}`,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {SECTORS.map(v => (
            <option key={v} value={v}>{v === 'ALL' ? 'All Sectors' : v}</option>
          ))}
        </select>

        <select
          value={sortBy}
          onChange={e => handleSort(e.target.value)}
          style={{
            padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            background: S.surface, color: S.text, border: `1px solid ${S.border2}`,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {SORT_OPTIONS.map(o => (
            <option key={o.key} value={o.key}>Sort: {o.label}</option>
          ))}
        </select>

        {hasFilters && (
          <button
            onClick={() => { handleSignalFilter('ALL'); handleSectorFilter('ALL'); }}
            style={{
              padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
              background: 'transparent', color: S.muted, border: `1px solid ${S.border2}`,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            ✕ Reset
          </button>
        )}

        <div style={{ flex: 1 }} />

        {isLoggedIn && (
          <ColumnPicker visibleCols={visibleCols} onToggleColumn={handleColToggle} />
        )}

        <SavedScansPanel
          isLoggedIn={isLoggedIn}
          onRequestInvite={onRequestInvite}
          currentFilters={{ signal: signalFilter, sector: sectorFilter, sort: sortBy, scanUsed: activeScan || null }}
          onLoadScan={handleLoadScan}
          onAlertsCountChange={signalHook.setAlertsCount}
        />
      </div>

      {/* ── Field registry filter panel (PIECE 2) ── */}
      <FilterPanel
        activeValues={registryFilters}
        onChange={handleFieldFilterChange}
        excludeKeys={['sector']}
      />

      {/* ── Market status banner ── */}
      {marketStatus && marketStatus !== 'open' && (
        <div style={{
          padding: '8px 14px', borderRadius: 8, marginBottom: 12,
          background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)',
          fontSize: 12, color: S.amber, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span>🕐</span>
          <span>
            {marketStatus === 'pre_market'  && 'Pre-market hours. Data from previous close.'}
            {marketStatus === 'after_hours' && 'After-hours. Showing closing prices.'}
            {marketStatus === 'closed'      && 'Market closed. Showing last session data.'}
            {marketStatus === 'weekend'     && 'Weekend. Showing Friday close data.'}
            {marketStatus === 'holiday'     && 'Market holiday. Showing prior session data.'}
          </span>
        </div>
      )}

      {isStale && dataAsOf && (
        <div style={{
          padding: '8px 14px', borderRadius: 8, marginBottom: 12,
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
          fontSize: 12, color: '#f87171', display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span>⚠️</span>
          <span>Data from {new Date(dataAsOf).toLocaleString('en-US', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })} — live data temporarily unavailable. Signals may not reflect current prices.</span>
        </div>
      )}

      {/* ── Results header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 13, color: S.muted }}>
          {loading ? 'Loading…' : `${displayCount} signal${displayCount !== 1 ? 's' : ''}`}
        </div>
        {!isLoggedIn && <CSVGate onRequestInvite={onRequestInvite} />}
        {isLoggedIn && results.length > 0 && (
          <button
            onClick={() => {
              const headers = ['Ticker','Company','Signal','Score','Price','Change %','Sector','Volume'];
              if (visibleCols.has('52w'))    headers.push('52W Pos%');
              if (visibleCols.has('volrat')) headers.push('Vol Ratio');
              const csv = [headers.join(','),
                ...results.map(r => {
                  const row = [`${r.ticker}`,"\""+r.name+"\"",r.signal,r.score,r.price,r.change,"\""+r.sector+"\"",r.volume];
                  if (visibleCols.has('52w'))    row.push(r.pos52w ?? '');
                  if (visibleCols.has('volrat')) row.push(r.avgVolume ? (r.volume/r.avgVolume).toFixed(2) : '');
                  return row.join(',');
                })
              ].join('\n');
              const blob = new Blob([csv], { type: 'text/csv' });
              const url  = URL.createObjectURL(blob);
              const a    = document.createElement('a');
              a.href = url; a.download = 'marketpulze-screener.csv'; a.click();
              URL.revokeObjectURL(url);
            }}
            style={{
              padding: '6px 16px', borderRadius: 6,
              border: `1px solid ${S.green}`, background: 'transparent',
              color: S.green, fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            ↓ Export CSV
          </button>
        )}
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, color: S.red, fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* ── Results table ── */}
      {!error && (
        <div style={{ background: S.bg, border: `1px solid ${S.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${S.border}` }}>
                  {[
                    { label: '#',      w: 40  },
                    { label: 'Ticker', w: 100 },
                    { label: 'Signal', w: 110 },
                    { label: 'Score',  w: 70  },
                    { label: 'Price',  w: 90  },
                    { label: 'Change', w: 90  },
                    { label: 'Sector', w: 120 },
                    { label: 'Volume', w: 90  },
                    ...(visibleCols.has('52w')    ? [{ label: '52W Pos%', w: 80 }] : []),
                    ...(visibleCols.has('volrat') ? [{ label: 'Vol Ratio', w: 80 }] : []),
                  ].map(h => (
                    <th key={h.label} style={{
                      textAlign: 'left', padding: '12px 14px', color: S.muted,
                      fontWeight: 700, fontSize: 11, letterSpacing: '0.04em',
                      width: h.w, whiteSpace: 'nowrap',
                    }}>
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={totalColCount} style={{ padding: '40px 16px', textAlign: 'center', color: S.muted, fontSize: 13 }}>
                      Scanning market signals…
                    </td>
                  </tr>
                )}

                {!loading && visibleResults.length === 0 && (
                  <tr>
                    <td colSpan={totalColCount} style={{ padding: '40px 16px', textAlign: 'center', color: S.muted, fontSize: 13 }}>
                      {hasFilters
                        ? 'No results match the current filters. Try resetting or choosing a different scan.'
                        : 'Loading screener data… this may take a moment on first visit.'
                      }
                    </td>
                  </tr>
                )}

                {!loading && visibleResults.map((row, i) => (
                  <tr
                    key={row.ticker}
                    onClick={() => handleTickerRowClick(row.ticker)}
                    style={{ borderBottom: `1px solid ${S.border}22`, cursor: 'pointer', transition: 'background 0.1s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = S.surface; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td style={{ padding: '12px 14px', color: S.subtle, fontSize: 11, fontFamily: 'monospace' }}>{i + 1}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: 800, fontSize: 14, color: S.text }}>{row.ticker}</div>
                      <div style={{ fontSize: 11, color: S.muted, marginTop: 2 }}>{row.name}</div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: 6,
                        fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                        color: signalColor(row.signal), background: signalBg(row.signal),
                      }}>
                        {row.signal}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <ScoreRing score={row.score} signal={row.signal} />
                    </td>
                    <td style={{ padding: '12px 14px', color: S.text, fontFamily: 'monospace', fontSize: 12 }}>{fmtPrice(row.price)}</td>
                    <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: row.change >= 0 ? S.green : S.red }}>{fmtChange(row.change)}</td>
                    <td style={{ padding: '12px 14px', color: S.muted, fontSize: 12 }}>{row.sector}</td>
                    <td style={{ padding: '12px 14px', color: S.muted, fontFamily: 'monospace', fontSize: 12 }}>{fmtVolume(row.volume)}</td>
                    {visibleCols.has('52w') && (
                      <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontSize: 12, color: row.pos52w != null ? (row.pos52w >= 70 ? S.green : row.pos52w <= 30 ? S.red : S.muted) : S.subtle }}>
                        {fmt52W(row.pos52w)}
                      </td>
                    )}
                    {visibleCols.has('volrat') && (
                      <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontSize: 12, color: volRatioColor(row.volume, row.avgVolume) }}>
                        {fmtVolRatio(row.volume, row.avgVolume)}
                      </td>
                    )}
                  </tr>
                ))}

                {!isLoggedIn && !loading && hiddenCount > 0 && (
                  <GuestLockRow onRequestInvite={onRequestInvite} colCount={totalColCount} />
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Legal disclaimer ── */}
      <div style={{
        padding: '12px 16px', background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.05)', borderRadius: 10,
        fontSize: 11, color: '#475569', lineHeight: 1.6,
      }}>
        <strong style={{ color: '#64748b' }}>Screener results are algorithmic signals — not investment advice.</strong>{' '}
        Signals are generated by MarketPulze algorithms based on price and volume data.
        They do not constitute a recommendation to buy or sell any security.
        Data delayed 15 minutes. Always conduct your own research before making investment decisions.
        MarketPulze is not a registered investment advisor or broker-dealer.
      </div>

    </div>
  );
}
